import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import AdminProjects, { EMPTY_DRAFT, draftIsActive } from "./AdminProjects";

const MAX_PHOTOS = 16;
const TOKEN_KEY = "admin_photo_token";

// Object URL for a locally chosen file, revoked when the preview goes away.
function Preview({ file, alt, ...props }) {
  const url = useMemo(() => URL.createObjectURL(file), [file]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  return <img src={url} alt={alt} {...props} />;
}

const badge = (text, color) => (
  <span
    style={{
      position: "absolute",
      top: 8,
      left: 8,
      background: color,
      color: "#fff",
      borderRadius: 4,
      padding: "2px 8px",
      fontSize: "0.75rem",
      zIndex: 2,
    }}
  >
    {text}
  </span>
);

function AdminPhotos() {
  const [token, setToken] = useState(() => {
    try {
      return sessionStorage.getItem(TOKEN_KEY) || "";
    } catch (e) {
      return "";
    }
  });
  const [tokenInput, setTokenInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0); // cache-buster after a save

  // Saved state from the server.
  const [photos, setPhotos] = useState([]);
  const [resume, setResume] = useState({ exists: false });
  const [projects, setProjects] = useState([]);

  // Pending changes, applied together by the Save button.
  const [photoAdds, setPhotoAdds] = useState([]); // File[]
  const [photoReplaces, setPhotoReplaces] = useState({}); // name -> File
  const [photoDeletes, setPhotoDeletes] = useState(new Set()); // names
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeDelete, setResumeDelete] = useState(false);
  const [projectDeletes, setProjectDeletes] = useState(new Set()); // ids
  const [draft, setDraft] = useState(EMPTY_DRAFT); // project being added or edited

  const uploadRef = useRef(null);
  const replaceRef = useRef(null);
  const replaceTarget = useRef("");
  const resumeRef = useRef(null);

  const api = useCallback(
    async (path, options = {}) => {
      const res = await fetch(`/api${path}`, {
        ...options,
        headers: { ...(options.headers || {}), "x-admin-token": token },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || `Request failed (${res.status})`);
      return body;
    },
    [token]
  );

  const loadAll = useCallback(async () => {
    const [p, r, pr] = await Promise.all([
      fetch("/api/photos").then((res) => res.json()),
      fetch("/api/resume/info").then((res) => res.json()),
      fetch("/api/projects").then((res) => res.json()),
    ]);
    setPhotos(p.items || []);
    setResume(r);
    setProjects(pr.items || []);
  }, []);

  useEffect(() => {
    if (!token) return;
    api("/admin/check")
      .then(() => {
        setAuthed(true);
        return loadAll();
      })
      .catch((e) => {
        setAuthed(false);
        setMessage(e.message);
      });
  }, [token, api, loadAll]);

  const draftActive = draftIsActive(draft);
  const pendingCount =
    photoAdds.length +
    Object.keys(photoReplaces).length +
    photoDeletes.size +
    (resumeFile ? 1 : 0) +
    (resumeDelete ? 1 : 0) +
    projectDeletes.size +
    (draftActive ? 1 : 0);

  // Warn before leaving the page with unsaved changes.
  useEffect(() => {
    if (!pendingCount) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [pendingCount]);

  const login = (e) => {
    e.preventDefault();
    setMessage("");
    try {
      sessionStorage.setItem(TOKEN_KEY, tokenInput);
    } catch (err) {
      /* sessionStorage unavailable; token stays in memory only */
    }
    setToken(tokenInput);
    setTokenInput("");
  };

  const logout = () => {
    if (pendingCount && !window.confirm("You have unsaved changes. Sign out anyway?")) return;
    try {
      sessionStorage.removeItem(TOKEN_KEY);
    } catch (err) {
      /* ignore */
    }
    setToken("");
    setAuthed(false);
    discard();
  };

  function discard() {
    setPhotoAdds([]);
    setPhotoReplaces({});
    setPhotoDeletes(new Set());
    setResumeFile(null);
    setResumeDelete(false);
    setProjectDeletes(new Set());
    setDraft(EMPTY_DRAFT);
  }

  const toggle = (setter, value) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });

  // ----- photos -----
  const remainingSlots = MAX_PHOTOS - (photos.length - photoDeletes.size) - photoAdds.length;
  const photoCount = photos.length - photoDeletes.size + photoAdds.length;

  const stageReplace = (name, file) => {
    setPhotoReplaces((prev) => ({ ...prev, [name]: file }));
    setPhotoDeletes((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };
  const unstageReplace = (name) =>
    setPhotoReplaces((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  const stageDeletePhoto = (name) => {
    unstageReplace(name);
    toggle(setPhotoDeletes, name);
  };

  // ----- resume -----
  const stageResume = (file) => {
    setResumeFile(file);
    setResumeDelete(false);
  };
  const stageResumeDelete = () => {
    setResumeFile(null);
    setResumeDelete((v) => !v);
  };

  // ----- projects -----
  const stageDeleteProject = (id) => {
    if (draft.editingId === id) setDraft(EMPTY_DRAFT);
    toggle(setProjectDeletes, id);
  };

  // ----- save -----
  const save = async () => {
    setMessage("");

    // Validate the project draft before touching anything.
    if (draftActive) {
      if (!draft.title.trim() || !draft.description.trim()) {
        setMessage("The project needs a title and a description.");
        return;
      }
      if (!draft.editingId && !draft.image) {
        setMessage("Choose an image for the new project.");
        return;
      }
    }

    // Deletes first so freed photo slots are available to the uploads.
    const steps = [];
    photoDeletes.forEach((name) =>
      steps.push({
        label: `delete ${name}`,
        run: () => api(`/admin/photos/${encodeURIComponent(name)}`, { method: "DELETE" }),
        done: () => setPhotoDeletes((p) => without(p, name)),
      })
    );
    Object.entries(photoReplaces).forEach(([name, file]) =>
      steps.push({
        label: `replace ${name}`,
        run: () => api(`/admin/photos/${encodeURIComponent(name)}`, { method: "PUT", body: fileForm(file) }),
        done: () => unstageReplace(name),
      })
    );
    if (resumeDelete)
      steps.push({
        label: "delete resume",
        run: () => api("/admin/resume", { method: "DELETE" }),
        done: () => setResumeDelete(false),
      });
    if (resumeFile)
      steps.push({
        label: "upload resume",
        run: () => api("/admin/resume", { method: "PUT", body: fileForm(resumeFile) }),
        done: () => setResumeFile(null),
      });
    projectDeletes.forEach((id) =>
      steps.push({
        label: "delete project",
        run: () => api(`/admin/projects/${id}`, { method: "DELETE" }),
        done: () => setProjectDeletes((p) => without(p, id)),
      })
    );
    if (draftActive)
      steps.push({
        label: draft.editingId ? `update project "${draft.title}"` : `add project "${draft.title}"`,
        run: () => {
          const body = new FormData();
          body.append("title", draft.title);
          body.append("description", draft.description);
          body.append("gh_link", draft.isOrganizational ? "" : draft.ghLink);
          body.append("demo_link", draft.demoLink);
          body.append("is_organizational", draft.isOrganizational ? "true" : "false");
          if (draft.image) body.append("image", draft.image);
          return api(draft.editingId ? `/admin/projects/${draft.editingId}` : "/admin/projects", {
            method: draft.editingId ? "PUT" : "POST",
            body,
          });
        },
        done: () => setDraft(EMPTY_DRAFT),
      });
    photoAdds.forEach((file) =>
      steps.push({
        label: `upload ${file.name}`,
        run: () => api("/admin/photos", { method: "POST", body: fileForm(file) }),
        done: () => setPhotoAdds((p) => p.filter((f) => f !== file)),
      })
    );

    setBusy(true);
    const failures = [];
    for (const step of steps) {
      try {
        await step.run();
        step.done();
      } catch (e) {
        failures.push(`Could not ${step.label}: ${e.message}`);
      }
    }
    try {
      await loadAll();
      setVersion((v) => v + 1);
    } catch (e) {
      failures.push("Saved, but could not refresh the lists. Reload the page.");
    }
    setBusy(false);
    setMessage(
      failures.length
        ? `${steps.length - failures.length} saved. ${failures.join(" ")} The failed changes are still pending.`
        : `Saved ${steps.length} change${steps.length === 1 ? "" : "s"}.`
    );
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  if (!authed) {
    return (
      <section className="photography-section">
        <div className="container py-4" style={{ maxWidth: 420 }}>
          <h2 className="project-heading">Admin</h2>
          <Form onSubmit={login}>
            <Form.Control
              type="password"
              placeholder="Admin token"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              autoComplete="off"
            />
            <Button type="submit" className="mt-3" disabled={!tokenInput}>
              Sign in
            </Button>
          </Form>
          {message && <p className="mt-3" style={{ color: "#ff8a8a" }}>{message}</p>}
        </div>
      </section>
    );
  }

  const text = { color: "var(--text-color)" };

  return (
    <section className="photography-section">
      <div className="container py-4">
        <h2 className="project-heading">Manage Photography</h2>
        <p style={text}>
          Changes below are queued. Nothing is saved until you click <strong>Save</strong> at the bottom.
        </p>
        <div className="mb-3">
          <Button variant="outline-light" onClick={logout}>
            Sign out
          </Button>
        </div>
        {message && <p style={text}>{message}</p>}

        {/* ----- Resume ----- */}
        <div className="mb-4" style={text}>
          <h4>Resume</h4>
          <p>
            {resume.exists
              ? `Current: uploaded ${new Date(resume.updated * 1000).toLocaleString()} (${Math.round(
                  resume.size / 1024
                )} KB)`
              : "No resume uploaded. The bundled resume is shown."}
          </p>
          {resumeFile && <p style={{ color: "#8bd48b" }}>Will upload: {resumeFile.name}</p>}
          {resumeDelete && <p style={{ color: "#ff8a8a" }}>Will be deleted.</p>}
          <Button disabled={busy} onClick={() => resumeRef.current.click()}>
            {resume.exists ? "Choose new resume" : "Choose resume"}
          </Button>{" "}
          {resumeFile && (
            <Button variant="outline-light" onClick={() => setResumeFile(null)}>
              Undo
            </Button>
          )}
          {resume.exists && !resumeFile && (
            <>
              <Button variant="outline-light" href={`/api/resume?v=${resume.updated}`} target="_blank">
                View
              </Button>{" "}
              <Button variant={resumeDelete ? "outline-light" : "danger"} disabled={busy} onClick={stageResumeDelete}>
                {resumeDelete ? "Undo delete" : "Delete"}
              </Button>
            </>
          )}
          <input
            ref={resumeRef}
            type="file"
            accept="application/pdf"
            hidden
            onChange={(e) => {
              const file = e.target.files[0];
              e.target.value = "";
              if (file) stageResume(file);
            }}
          />
        </div>

        {/* ----- Projects ----- */}
        <AdminProjects
          projects={projects}
          version={version}
          draft={draft}
          setDraft={setDraft}
          deletes={projectDeletes}
          onToggleDelete={stageDeleteProject}
          busy={busy}
        />

        {/* ----- Photos ----- */}
        <h4 style={text}>Photos</h4>
        <p style={text}>
          {photoCount} / {MAX_PHOTOS} photos &middot; jpg, png, gif, webp, bmp &middot; max 10 MB each
        </p>
        <div className="mb-3">
          <Button
            disabled={busy || remainingSlots <= 0}
            onClick={() => uploadRef.current.click()}
            title={remainingSlots <= 0 ? "Photo limit reached. Replace or delete one." : ""}
          >
            Choose photos
          </Button>
          <input
            ref={uploadRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => {
              const files = Array.from(e.target.files).slice(0, Math.max(remainingSlots, 0));
              e.target.value = "";
              if (files.length) setPhotoAdds((prev) => [...prev, ...files]);
            }}
          />
          <input
            ref={replaceRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              const file = e.target.files[0];
              e.target.value = "";
              if (file) stageReplace(replaceTarget.current, file);
            }}
          />
        </div>
        <div className="photography-grid">
          {photos.map((p) => {
            const deleted = photoDeletes.has(p.name);
            const replacement = photoReplaces[p.name];
            return (
              <div key={p.name} className="photography-item" style={{ opacity: deleted ? 0.4 : 1 }}>
                {deleted && badge("Will be deleted", "#b02a37")}
                {replacement && badge("Will be replaced", "#b8860b")}
                {replacement ? (
                  <Preview file={replacement} alt={p.name} className="photography-image" />
                ) : (
                  <img src={`${p.url}?v=${version}`} alt={p.name} className="photography-image" />
                )}
                <div className="photography-overlay" style={{ transform: "translateY(0)" }}>
                  <h4 className="photography-title">{p.name}</h4>
                  {!deleted && (
                    <>
                      <Button
                        size="sm"
                        disabled={busy}
                        onClick={() => {
                          replaceTarget.current = p.name;
                          replaceRef.current.click();
                        }}
                      >
                        Replace
                      </Button>{" "}
                      {replacement && (
                        <>
                          <Button size="sm" variant="outline-light" onClick={() => unstageReplace(p.name)}>
                            Undo
                          </Button>{" "}
                        </>
                      )}
                    </>
                  )}
                  <Button
                    size="sm"
                    variant={deleted ? "outline-light" : "danger"}
                    disabled={busy}
                    onClick={() => stageDeletePhoto(p.name)}
                  >
                    {deleted ? "Undo delete" : "Delete"}
                  </Button>
                </div>
              </div>
            );
          })}
          {photoAdds.map((file, i) => (
            <div key={`${file.name}-${i}`} className="photography-item">
              {badge("New", "#2e7d32")}
              <Preview file={file} alt={file.name} className="photography-image" />
              <div className="photography-overlay" style={{ transform: "translateY(0)" }}>
                <h4 className="photography-title">{file.name}</h4>
                <Button
                  size="sm"
                  variant="outline-light"
                  onClick={() => setPhotoAdds((prev) => prev.filter((f) => f !== file))}
                >
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* ----- Save ----- */}
        <div className="mt-4 pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.2)", ...text }}>
          <p>
            {pendingCount
              ? `${pendingCount} unsaved change${pendingCount === 1 ? "" : "s"}.`
              : "No unsaved changes."}
          </p>
          <Button size="lg" disabled={busy || !pendingCount} onClick={save}>
            {busy ? "Saving..." : "Save"}
          </Button>{" "}
          <Button
            size="lg"
            variant="outline-light"
            disabled={busy || !pendingCount}
            onClick={() => {
              if (window.confirm("Discard all unsaved changes?")) discard();
            }}
          >
            Discard
          </Button>
        </div>
      </div>
    </section>
  );
}

function fileForm(file) {
  const form = new FormData();
  form.append("file", file);
  return form;
}

function without(set, value) {
  const next = new Set(set);
  next.delete(value);
  return next;
}

export default AdminPhotos;
